<template>
  <div class="ops-overview">
    <header class="ops-overview__head">
      <h1>{{ greeting }}</h1>
      <p class="ops-overview__standfirst">
        <template v-if="loading && !summary">Loading the estate…</template>
        <template v-else-if="!summary">
          Couldn't reach the platform API — nothing below is current.
        </template>
        <template v-else-if="servers.total === 0">
          No servers enrolled yet.
          <router-link to="/servers">Enrol your first server →</router-link>
        </template>
        <template v-else>
          {{ servers.reporting }} of {{ servers.total }}
          {{ servers.total === 1 ? 'server' : 'servers' }} reporting
          <span v-if="lastTelemetryLabel"> · last telemetry {{ lastTelemetryLabel }}</span>
          <span v-else> · no telemetry received yet</span>
        </template>
      </p>
    </header>

    <!-- The single most important element on this page. A monitoring platform
         whose alerts reach nobody is a dashboard, and nothing else in the UI
         said so. Disappears entirely the moment any channel exists. -->
    <div v-if="showChannelBanner" class="ops-overview__banner">
      <span class="ops-overview__banner-pill">Action</span>
      <div class="ops-overview__banner-body">
        <strong>Alerts aren't reaching anyone.</strong>
        No notification channel is configured, so
        {{ alerts.open === 1 ? 'the 1 open alert' : `all ${alerts.open} open alerts` }}<span
          v-if="alerts.serious_or_worse > 0"
        > — including {{ alerts.serious_or_worse }} serious</span> exist only in this dashboard.
        <router-link class="ops-overview__banner-cta" to="/servers/alerts">Add a channel →</router-link>
      </div>
    </div>

    <!-- The quieter sibling: channels exist but every one of them is off. -->
    <div v-else-if="showChannelsDisabledBanner" class="ops-overview__banner ops-overview__banner--warn">
      <span class="ops-overview__banner-pill">Check</span>
      <div class="ops-overview__banner-body">
        <strong>Every notification channel is disabled.</strong>
        {{ alerts.channels_configured }} configured, none enabled — alerts will not be delivered.
        <router-link class="ops-overview__banner-cta" to="/servers/alerts">Review channels →</router-link>
      </div>
    </div>

    <!-- Plan 012 §7.6 — the digest checks itself. Every digest is sent even
         on a quiet day, so its absence is a fault (usually the dispatcher
         cron), not good news. Deliberately a banner state, not an alert: an
         alert about the notifier would travel through the notifier. -->
    <div v-else-if="showDigestOverdueBanner" class="ops-overview__banner ops-overview__banner--warn">
      <span class="ops-overview__banner-pill">Check</span>
      <div class="ops-overview__banner-body">
        <strong>No digest has been sent for 26 hours.</strong>
        <template v-if="alerts.digest?.last_completed_at"> The last one went out {{ relativeAge(Math.max(0, Math.floor(Date.now() / 1000) - alerts.digest.last_completed_at)) }}.</template>
        The notification dispatcher may have stopped — repeats and delayed alerts depend on it too.
        <router-link class="ops-overview__banner-cta" to="/servers/alerts">Check channels →</router-link>
      </div>
    </div>

    <div class="ops-overview__kpis">
      <router-link
        v-for="tile in tiles"
        :key="tile.key"
        class="ops-overview__kpi"
        :class="`ops-overview__kpi--${tile.tone}`"
        :to="tile.to"
      >
        <div class="ops-overview__kpi-value">{{ tile.value }}</div>
        <div class="ops-overview__kpi-label">{{ tile.label }}</div>
        <div class="ops-overview__kpi-sub">{{ tile.sub }}</div>
      </router-link>
    </div>

    <div class="ops-overview__grid">
      <section class="ops-overview__panel">
        <header class="ops-overview__panel-head">
          <span>Needs attention</span>
          <!-- Plan 012 §8.4 — snoozed alerts are held by a human: out of
               the tiles and this queue, counted here. -->
          <router-link v-if="alerts.snoozed" class="ops-overview__panel-note" to="/servers/alerts?status=snoozed">{{ alerts.snoozed }} snoozed</router-link>
          <router-link class="ops-overview__panel-action" to="/servers/alerts">View all →</router-link>
        </header>

        <p v-if="!summary && !loading" class="ops-overview__empty">
          Unavailable while the API is unreachable.
        </p>
        <p v-else-if="attention.length === 0 && !loading" class="ops-overview__empty">
          Nothing open. No alerts, no detections, and every integration swept recently.
        </p>

        <router-link
          v-for="row in attention"
          :key="`${row.kind}-${row.alert_id ?? row.title}`"
          class="ops-overview__row"
          :to="row.href"
        >
          <span class="ops-overview__stripe" :class="`is-${severityTone(row.severity)}`"></span>
          <span class="ops-overview__row-body">
            <span class="ops-overview__row-title">{{ row.title }}</span>
            <span class="ops-overview__row-meta">
              <span v-if="row.server" class="ops-overview__row-server">{{ row.server }}</span>
              <span v-if="row.server && row.detail"> · </span>
              <span v-if="row.detail">{{ row.detail }}</span>
              <span v-if="row.age_seconds !== null"> · {{ relativeAge(row.age_seconds) }}</span>
            </span>
          </span>
          <span class="ops-overview__kind">{{ kindLabel(row.kind) }}</span>
          <span class="ops-overview__pill" :class="`is-${severityTone(row.severity)}`">
            {{ severityLabel(row.severity) }}
          </span>
        </router-link>
      </section>

      <section class="ops-overview__panel">
        <header class="ops-overview__panel-head">
          <span>Fleet pressure</span>
          <router-link class="ops-overview__panel-action" to="/servers/pressure">Details →</router-link>
        </header>

        <p v-if="pressureRows.length === 0 && !loading" class="ops-overview__empty">
          No host resource telemetry yet — servers running an agent older than
          2026-08-01 don't report it.
        </p>

        <router-link
          v-for="row in pressureRows"
          :key="row.server_id"
          class="ops-overview__prow"
          :to="`/servers/${row.server_id}`"
        >
          <span class="ops-overview__prow-name">{{ row.server }}</span>
          <span class="ops-overview__bar">
            <span
              class="ops-overview__bar-fill"
              :class="`is-${loadTone(row.load_per_core)}`"
              :style="{ width: `${loadWidth(row.load_per_core)}%` }"
            ></span>
          </span>
          <span class="ops-overview__prow-value">
            {{ row.load_per_core !== null ? row.load_per_core.toFixed(2) : '—' }}
          </span>
        </router-link>

        <div v-if="highestDisk" class="ops-overview__prow ops-overview__prow--callout">
          <span class="ops-overview__prow-callout-label">
            Highest disk — {{ highestDisk.server }}
          </span>
          <span class="ops-overview__pill" :class="`is-${pctTone(highestDisk.disk_pct)}`">
            {{ Math.round(highestDisk.disk_pct) }}%
          </span>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
/**
 * The operations Overview — the front door at `/`, mounted by
 * `app\module\shell\Hook\DashboardHomeAlterHook` via the
 * `OperationsOverviewBoard` PHP component.
 *
 * Replaces the Phase 1 tile grid, whose four headline tiles all rendered "—"
 * and whose largest panel listed development phases. The organising principle
 * from the 2026-08-01 review is **what needs me, in what order**: anything
 * requiring action appears above anything merely informational, and every
 * number links into the thing it counts.
 *
 * Reads exactly one endpoint (`/api/v1/shell/operations/summary`) through the
 * shared TTL cache, so mounting this page alongside the sidebar's badges costs
 * one request, not two.
 *
 * **Nothing here fabricates a figure.** A count the API returned as null
 * renders an em-dash and a panel with no rows renders a sentence explaining
 * why — the review's standing criticism of the old dashboard was four tiles
 * confidently showing "—" with no explanation, and an empty state that says
 * nothing is the same failure in a different shape.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import { fetchOperationsSummary, peekOperationsSummary } from '@root/services/operationsSummary';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/shell/operations' },
  greetingName: { type: String, default: '' },
});

/** Live enough to notice an incident, calm enough not to flicker while read. */
const POLL_MS = 30000;

const summary = ref(peekOperationsSummary());
const loading = ref(summary.value === null);
let pollHandle = null;

async function load({ force = true } = {}) {
  const data = await fetchOperationsSummary({ force });
  summary.value = data;
  loading.value = false;
}

onMounted(() => {
  load({ force: false });
  pollHandle = setInterval(() => load({ force: true }), POLL_MS);
});

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
});

// ── derived slices, each defaulting to an honest empty shape ──────────────

const servers = computed(() => summary.value?.servers ?? { total: 0, reporting: 0, healthy: 0, last_telemetry_age_seconds: null });
const alerts = computed(() => summary.value?.alerts ?? { open: 0, serious_or_worse: 0, by_severity: {}, channels_configured: 0, channels_enabled: 0, newest_serious: null });
const domains = computed(() => summary.value?.domains ?? { total: 0, expiring_soon: 0, expiring_window_days: 60 });
const uptime = computed(() => summary.value?.uptime ?? { uptime_pct: null, server_count: 0 });
const attention = computed(() => summary.value?.attention ?? []);
const pressureRows = computed(() => summary.value?.pressure?.servers ?? []);
const highestDisk = computed(() => {
  const row = summary.value?.pressure?.highest_disk;
  return row && row.disk_pct !== null ? row : null;
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : (hour < 18 ? 'afternoon' : 'evening');

  return props.greetingName
    ? `Good ${part}, ${props.greetingName}.`
    : `Good ${part}.`;
});

const lastTelemetryLabel = computed(() => {
  const age = servers.value.last_telemetry_age_seconds;
  return age === null || age === undefined ? '' : relativeAge(age);
});

/**
 * The banner is gated on `channels_configured`, not `channels_enabled`: a
 * deliberately-disabled channel is an operator decision, not the silent gap
 * this banner exists to shout about. That case gets its own quieter banner.
 * Both stay hidden while the payload is still loading — a banner that
 * appears and then vanishes is worse than one that appears a beat late.
 */
const showChannelBanner = computed(() => summary.value !== null && alerts.value.channels_configured === 0);
const showChannelsDisabledBanner = computed(() => summary.value !== null
  && alerts.value.channels_configured > 0
  && alerts.value.channels_enabled === 0);
const showDigestOverdueBanner = computed(() => summary.value !== null && alerts.value.digest?.overdue === true);

/** "3 servers" reads better than "5 warnings across 5 warnings". */
const warningsSub = computed(() => {
  const serverIds = new Set(
    attention.value
      .filter((row) => row.severity === 'warning' && row.server_id !== null)
      .map((row) => row.server_id),
  );
  if (serverIds.size === 0) {
    return alerts.value.by_severity?.warning ? 'not server-scoped' : 'none open';
  }

  return `${serverIds.size} ${serverIds.size === 1 ? 'server' : 'servers'}`;
});

/**
 * Counts of things you can act on, not status words — and every one of them
 * is a link to the filtered view of what it counts.
 */
const tiles = computed(() => {
  const bySeverity = alerts.value.by_severity || {};
  const warnings = bySeverity.warning ?? 0;
  const serious = alerts.value.serious_or_worse ?? 0;
  const newest = alerts.value.newest_serious;

  return [
    {
      key: 'serious',
      value: serious,
      label: serious === 1 ? 'Serious alert' : 'Serious alerts',
      sub: newest
        ? `${newest.server ?? 'fleet'} · ${relativeAge(newest.age_seconds)}`
        : 'none open',
      tone: serious > 0 ? 'bad' : 'good',
      to: '/servers/alerts?severity=serious',
    },
    {
      key: 'warnings',
      value: warnings,
      label: warnings === 1 ? 'Warning' : 'Warnings',
      sub: warningsSub.value,
      tone: warnings > 0 ? 'warn' : 'good',
      to: '/servers/alerts?severity=warning',
    },
    {
      key: 'agents',
      value: `${servers.value.healthy}/${servers.value.total}`,
      label: 'Agents healthy',
      sub: servers.value.reporting === servers.value.total
        ? 'all reporting'
        : `${servers.value.total - servers.value.reporting} not reporting`,
      tone: servers.value.healthy === servers.value.total && servers.value.total > 0 ? 'good' : 'warn',
      to: '/servers',
    },
    {
      key: 'domains',
      value: domains.value.expiring_soon,
      label: 'Domains expiring',
      sub: `within ${domains.value.expiring_window_days} days · ${domains.value.total} owned`,
      tone: domains.value.expiring_soon > 0 ? 'warn' : 'good',
      to: '/domains?sort=expires_at&direction=asc',
    },
    {
      key: 'uptime',
      value: uptime.value.uptime_pct === null ? '—' : `${uptime.value.uptime_pct.toFixed(2)}%`,
      label: 'Fleet uptime 30d',
      sub: uptime.value.uptime_pct === null
        ? 'no rollups computed yet'
        : `${uptime.value.server_count} ${uptime.value.server_count === 1 ? 'server' : 'servers'}`,
      tone: 'lead',
      to: '/servers',
    },
  ];
});

// ── formatting ────────────────────────────────────────────────────────────

function severityTone(severity) {
  if (severity === 'critical' || severity === 'serious') return 'bad';
  if (severity === 'warning') return 'warn';
  return 'info';
}

function severityLabel(severity) {
  return severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Unknown';
}

function kindLabel(kind) {
  return { detection: 'Detection', integration: 'Integration', domain: 'Domain', alert: 'Alert' }[kind] || 'Alert';
}

function relativeAge(seconds) {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

/**
 * Load bar scale. Full width at 1.25 load-per-core — luna-node's
 * `host.cpu_saturation` threshold — so a full bar and a firing alert mean the
 * same thing rather than two different things that look alike.
 */
function loadWidth(loadPerCore) {
  if (loadPerCore === null || loadPerCore === undefined) return 0;
  return Math.max(2, Math.min(100, (loadPerCore / 1.25) * 100));
}

function loadTone(loadPerCore) {
  if (loadPerCore === null || loadPerCore === undefined) return 'unknown';
  if (loadPerCore >= 1.25) return 'bad';
  if (loadPerCore >= 0.75) return 'warn';
  return 'good';
}

/** Amber from 75%, red from 90% — same ramp `ServerWebPressurePanel.vue` uses. */
function pctTone(pct) {
  if (pct === null || pct === undefined) return 'unknown';
  if (pct >= 90) return 'bad';
  if (pct >= 75) return 'warn';
  return 'good';
}
</script>

<style lang="scss">
.ops-overview {
  &__head {
    margin-bottom: 18px;

    h1 {
      font-size: 22px;
      font-weight: 600;
      margin: 0 0 4px;
      letter-spacing: -0.01em;
    }
  }

  &__standfirst {
    font-size: 13px;
    color: var(--theme-text-secondary);
    margin: 0;

    a {
      color: var(--theme-accent-dark);
      font-weight: 600;
    }
  }

  &__banner {
    display: flex;
    gap: 11px;
    align-items: flex-start;
    padding: 12px 14px;
    border-radius: var(--theme-radius-lg, 10px);
    background: #fbe9e6;
    border: 1px solid rgba(209, 67, 67, 0.35);
    margin-bottom: 16px;
    font-size: 13px;
    color: var(--theme-text-primary);

    &--warn {
      background: #fdf1de;
      border-color: rgba(224, 160, 48, 0.4);
    }
  }

  &__banner-pill {
    flex: none;
    font-size: 9.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 20px;
    background: var(--theme-status-danger);
    color: #fff;

    .ops-overview__banner--warn & {
      background: var(--theme-status-warning);
    }
  }

  &__banner-body {
    line-height: 1.5;
  }

  &__banner-cta {
    color: var(--theme-accent-dark);
    font-weight: 700;
    white-space: nowrap;
    text-decoration: none;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
  }

  &__kpi {
    position: relative;
    display: block;
    overflow: hidden;
    padding: 13px 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    text-decoration: none !important;
    color: inherit;
    transition: box-shadow 0.12s, transform 0.12s;

    &:hover {
      box-shadow: var(--theme-shadow-panel);
      transform: translateY(-1px);
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--bad::before  { background: var(--theme-status-danger); }
    &--bad  .ops-overview__kpi-value { color: #a53232; }
    &--warn::before { background: var(--theme-status-warning); }
    &--warn .ops-overview__kpi-value { color: #8a5a0d; }
    &--good::before { background: var(--theme-status-success); }
    &--lead::before { background: var(--theme-accent-primary); }
  }

  &__kpi-value {
    font-size: 24px;
    font-weight: 660;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
  }

  &__kpi-label {
    font-size: 11.5px;
    color: var(--theme-text-secondary);
    margin-top: 2px;
  }

  &__kpi-sub {
    font-family: var(--theme-font-mono);
    font-size: 10px;
    color: var(--theme-text-tertiary);
    margin-top: 5px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1.55fr 1fr;
    gap: 14px;
    align-items: start;

    @media (max-width: 1000px) { grid-template-columns: 1fr; }
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__panel-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 11px 14px;
    border-bottom: 1px solid var(--theme-border);
    font-size: 12.5px;
    font-weight: 620;
  }

  &__panel-action {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-accent-dark);
    text-decoration: none;
    font-weight: 600;
  }

  &__panel-note {
    margin-left: auto;
    margin-right: 10px;
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    text-decoration: none;
  }

  &__empty {
    padding: 16px 14px;
    margin: 0;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);
  }

  &__row {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 9px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-size: 12.5px;
    color: inherit;
    text-decoration: none !important;

    &:last-child { border-bottom: 0; }
    &:hover { background: var(--theme-bg-content); }
  }

  &__stripe {
    width: 3px;
    align-self: stretch;
    border-radius: 2px;
    flex: none;

    &.is-bad  { background: var(--theme-status-danger); }
    &.is-warn { background: var(--theme-status-warning); }
    &.is-info { background: var(--theme-status-info); }
  }

  &__row-body {
    flex: 1;
    min-width: 0;
  }

  &__row-title {
    display: block;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-meta {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 2px;
  }

  &__row-server { font-weight: 600; }

  &__kind {
    flex: none;
    font-family: var(--theme-font-mono);
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--theme-text-tertiary);
  }

  &__pill {
    flex: none;
    font-size: 9.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 2.5px 8px;
    border-radius: 20px;
    font-weight: 700;
    white-space: nowrap;

    &.is-bad  { background: #fbe9e6; color: #a53232; }
    &.is-warn { background: #fdf1de; color: #8a5a0d; }
    &.is-info { background: var(--theme-border-soft); color: var(--theme-text-secondary); }
    &.is-good { background: #e6f2ec; color: #1c7a4d; }
    &.is-unknown { background: var(--theme-border-soft); color: var(--theme-text-tertiary); }
  }

  &__prow {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 9px 14px;
    border-bottom: 1px solid var(--theme-border-soft);
    font-size: 12.5px;
    color: inherit;
    text-decoration: none !important;

    &:last-child { border-bottom: 0; }
    &:hover { background: var(--theme-bg-content); }

    &--callout {
      border-top: 1px solid var(--theme-border);
      background: var(--theme-bg-content);

      &:hover { background: var(--theme-bg-content); }
    }
  }

  &__prow-name {
    font-family: var(--theme-font-mono);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__prow-callout-label {
    flex: 1;
    font-size: 11.5px;
    color: var(--theme-text-secondary);
  }

  &__prow-value {
    flex: 0 0 38px;
    text-align: right;
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-tertiary);
    font-variant-numeric: tabular-nums;
  }

  &__bar {
    flex: 0 0 78px;
    height: 5px;
    border-radius: 3px;
    background: var(--theme-border-soft);
    overflow: hidden;
    display: block;
  }

  &__bar-fill {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: var(--theme-status-success);

    &.is-warn { background: var(--theme-status-warning); }
    &.is-bad  { background: var(--theme-status-danger); }
    &.is-unknown { background: var(--theme-border-strong); }
  }
}
</style>
